"use client";

/**
 * Карусель городов на главной (вместо блоков «Долгосрочная аренда» /
 * «Транспортные услуги»). Карточка города: картинка, название сверху, кол-во
 * машин в автопарке и «от N ₽». Работает как выбор города: клик — показать
 * автопарк этого города в каталоге, повторный клик по активному — назад ко
 * всему автопарку. Сама карусель — как блок «Популярные автомобили» (классы).
 */

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import CarouselArrow from "./CarouselArrow";
import { useSearch } from "./SearchProvider";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

export default function CitiesCarousel() {
  const { cityCards, cities, activeCity, selectCity } = useSearch();
  const { locale } = useLocale();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 1);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    update();
    const el = trackRef.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, cityCards.length]);

  const scrollBy = (delta: number) => trackRef.current?.scrollBy({ left: delta, behavior: "smooth" });

  if (!cityCards.length) return null;

  const onCity = (station: string) => {
    if (activeCity?.station === station) { selectCity(null); return; }
    const c = cities.find((x) => x.station === station);
    if (c) {
      selectCity(c);
      track("city_select", { city: c.slug });
      // прокрутка к каталогу, чтобы было видно автопарк выбранного города
      if (typeof document !== "undefined") document.getElementById("rent")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="flex snap-x gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {cityCards.map((c) => {
          const active = activeCity?.station === c.station;
          return (
            <button
              key={c.station}
              type="button"
              onClick={() => onCity(c.station)}
              aria-pressed={active}
              title={`${locale === "en" ? "Fleet" : "Автопарк"} — ${c.name}`}
              className="group relative h-[150px] w-[230px] shrink-0 snap-start cursor-pointer overflow-hidden rounded-3xl text-left transition"
            >
              {/* city photo */}
              {c.image ? (
                <Image
                  src={c.image}
                  alt={c.name}
                  fill
                  sizes="230px"
                  className="object-cover transition-transform duration-300 group-hover:scale-[1.05]"
                />
              ) : (
                <div className="absolute inset-0 bg-surface-2" />
              )}
              {/* gradient for legibility */}
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/30" />

              {/* name top */}
              <span className="absolute left-4 right-4 top-3 text-[20px] font-bold leading-tight text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
                {c.name}
              </span>

              {/* count + price bottom */}
              <div className="absolute inset-x-4 bottom-3 flex items-end justify-between gap-2">
                <span className="rounded-full bg-white/20 px-2.5 py-1 text-[13px] font-semibold leading-none text-white backdrop-blur-sm">
                  {c.count} {locale === "en" ? "cars" : "авто"}
                </span>
                <span className="flex items-baseline gap-1 whitespace-nowrap text-white">
                  <span className="text-[12px] font-medium opacity-80">{locale === "en" ? "from" : "от"}</span>
                  <span className="text-[18px] font-bold leading-none">{c.priceFrom}</span>
                </span>
              </div>

              {/* Рамка выбранного города — отдельным оверлеем ПОВЕРХ фото
                  (inset-кольцо пряталось под картинкой). Без сдвига макета. */}
              {active && (
                <span aria-hidden className="pointer-events-none absolute inset-0 z-20 rounded-3xl ring-[3px] ring-inset ring-primary" />
              )}
            </button>
          );
        })}
      </div>

      {canLeft && (
        <CarouselArrow
          direction="left"
          ariaLabel={locale === "en" ? "Back" : "Назад"}
          onClick={() => scrollBy(-240)}
          className="!rounded-full absolute left-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]"
        />
      )}
      {canRight && (
        <CarouselArrow
          direction="right"
          ariaLabel={locale === "en" ? "Next" : "Вперед"}
          onClick={() => scrollBy(240)}
          className="!rounded-full absolute right-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]"
        />
      )}
    </div>
  );
}
